import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import {
  GetPatientGradeApi,
  UpdatePatientGradeApi,
} from '../../services/CampManagementService'
import { toast } from 'react-toastify'

const styles = () => ({
  containerBox: {
    backgroundColor: '#f9f9f9',
    padding: '16px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    margin: '10px 16px',
  },
  rootRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
    flexWrap: 'wrap' as const,
    gap: '8px',
  },
  fieldLabel: {
    fontWeight: '600',
    fontSize: '14px',
    minWidth: '120px',
  },
  gradeContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  formControlGap: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  innerBox: {
    flex: 1,
    maxWidth: '100%',
  },
  radioGroup: {
    gap: '16px',
  },
  radioLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: '14px',
    },
  },
  notesField: {
    marginTop: '8px',
  },
  textFieldStyle: {
    '& .MuiOutlinedInput-root': {
      fontSize: '14px',
    },
  },
  submitButton: {
    textTransform: 'none',
    fontWeight: 600,
    marginTop: '12px',
    alignSelf: 'flex-end',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
})

const HealthGrade = ({ appointmentData }: any) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [grade, setGrade] = useState('')
  const [additionalNotes, setAdditionalNotes] = useState('')

  useEffect(() => {
    getPatientGrade()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getPatientGrade = async () => {
    try {
      const response: any = await GetPatientGradeApi(
        appointmentData?.muhId,
        appointmentData?.campId
      )

      if (response?.success) {
        setGrade(response?.data[0]?.grade || '')
        setAdditionalNotes(response?.data[0]?.note || '')
      } else {
        toast.error(
          response?.message ||
            'Failed to fetch grade and notes. Please try again.'
        )
      }
    } catch (error: any) {
      console.error('Error fetching grade and notes:', error)
      toast.error(
        error?.message || 'An error occurred while fetching. Please try again.'
      )
    }
  }

  const handleSubmit = async () => {
    console.log('Submitted Rating:', {
      appointmentId: appointmentData?.id,
      grade,
      notes: additionalNotes,
    })
    const payload = {
      patientUhid: appointmentData?.muhId,
      campId: appointmentData?.campId,
      grade: grade,
      note: additionalNotes,
    }
    try {
      const response: any = await UpdatePatientGradeApi(payload)

      if (response?.success) {
        toast.success(
          response?.message || 'Grade and notes submitted successfully'
        )
        getPatientGrade()
      } else {
        toast.error(
          response?.message ||
            'Failed to submit grade and notes. Please try again.'
        )
      }
    } catch (error: any) {
      console.error('Error submitting grade and notes:', error)
      toast.error(
        error?.message ||
          'An error occurred while submitting. Please try again.'
      )
    }
  }

  return (
    <Box className={`${classes.containerBox} ${classes.rootRow}`}>
      <Typography className={classes?.fieldLabel}>Grade & Notes</Typography>
      <Box className={`${classes.gradeContainer} ${classes.innerBox}`}>
        <FormControl
          component='fieldset'
          fullWidth
          className={classes.formControlGap}
        >
          <RadioGroup
            row
            aria-label='grade'
            name='grade-radio-buttons-group'
            value={grade}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setGrade(e?.target?.value)
            }
            className={classes.radioGroup}
          >
            {['a', 'b', 'c', 'd', 'e']?.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio size='small' />}
                label={option?.toUpperCase()}
                className={classes.radioLabel}
              />
            ))}
          </RadioGroup>

          <TextField
            fullWidth
            margin='normal'
            label='Additional Notes'
            multiline
            rows={3}
            variant='outlined'
            value={additionalNotes}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setAdditionalNotes(e.target.value)
            }
            placeholder='Enter any additional notes here...'
            InputProps={{
              className: 'doctor-review-input',
            }}
            className={`${classes.notesField} ${classes.textFieldStyle}`}
          />
          <Box className={classes.buttonRow}>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={handleSubmit}
              className={classes.submitButton}
              disabled={!grade}
            >
              Submit Rating
            </Button>
          </Box>
        </FormControl>
      </Box>
    </Box>
  )
}

export default HealthGrade
